import http from '../utils/request'

/**
 * 文档中心接口
 * 对应后端 DocumentController (/api/document)
 */

// ============ 文档目录管理 ============

/**
 * 获取企业云盘目录树
 * @returns {Promise} 返回目录树结构
 */
export const getCompanyFolderTree = () => http.get('/document/folder/company')

/**
 * 获取我的云盘目录树
 * @returns {Promise} 返回目录树结构
 */
export const getMyFolderTree = () => http.get('/document/folder/my')

/**
 * 保存目录（新增或编辑）
 * @param {Object} data - 目录信息 { guid, name, parentGuid, folderType }
 * @returns {Promise}
 */
export const saveFolder = (data) => http.post('/document/folder', data)

/**
 * 删除目录
 * @param {string} guid - 目录GUID
 * @returns {Promise}
 */
export const deleteFolder = (guid) => http.delete(`/document/folder/${guid}`)

// ============ 文档文件管理 ============

/**
 * 获取指定目录下的文件列表
 * @param {string} catalogGuid - 目录GUID
 * @param {string} category - 分类(CompanyBox/MyBox)
 * @param {Object} filters - 过滤条件 { search, fileType }
 * @returns {Promise}
 */
export const getFilesList = (catalogGuid, category, filters = {}) => {
  return http.get('/document/files', {
    params: {
      catalogGuid,
      category,
      ...filters
    }
  })
}

/**
 * 获取文件详情
 * @param {string} guid - 文件GUID
 * @returns {Promise}
 */
export const getFileDetail = (guid) => http.get(`/document/files/${guid}`)

/**
 * 上传文件
 * @param {FormData} formData - 包含文件和元数据的FormData对象
 * @returns {Promise}
 */
export const uploadFile = (formData) => {
  return http.post('/document/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

/**
 * 更新文件信息（不含文件内容）
 * @param {Object} data - 文件信息
 * @returns {Promise}
 */
export const updateFileInfo = (data) => http.put('/document/files', data)

/**
 * 删除文件（移至回收站）
 * @param {string} guids - 文件GUID，多个用逗号分隔
 * @returns {Promise}
 */
export const deleteFile = (guids) => http.delete(`/document/files/${guids}`)

/**
 * 移动文件
 * @param {Object} data - { folderGuid, filesGuid, title }
 * @returns {Promise}
 */
export const moveFile = (data) => http.post('/document/files/move', data)

/**
 * 复制文件
 * @param {Object} data - { folderGuid, filesGuid, title }
 * @returns {Promise}
 */
export const copyFile = (data) => http.post('/document/files/copy', data)

/**
 * 下载文件
 * @param {string} guid - 文件GUID
 * @param {string} title - 文件名
 * @param {string} savePath - 保存路径
 * @returns {string} 下载URL
 */
export const getDownloadUrl = (guid, title, savePath) => {
  const baseUrl = window.__APP_CONFIG__?.API_BASE_URL || 'http://localhost:5260/api'
  const params = new URLSearchParams({ guid, title, savePath })
  return `${baseUrl}/document/files/download?${params.toString()}`
}

// ============ 文档版本管理 ============

/**
 * 获取文档历史版本列表
 * @param {string} guid - 当前文档GUID
 * @param {string} catalogGuid - 目录GUID
 * @returns {Promise}
 */
export const getFileVersions = (guid, catalogGuid) => {
  return http.get('/document/files/versions', {
    params: { guid, catalogGuid }
  })
}

/**
 * 上传新版本
 * @param {FormData} formData - 包含文件和版本信息的FormData
 * @returns {Promise}
 */
export const uploadNewVersion = (formData) => {
  return http.post('/document/files/version/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

// ============ 文档共享管理 ============

/**
 * 获取共享给我的文件列表
 * @returns {Promise}
 */
export const getSharedFiles = () => http.get('/document/files/shared')

/**
 * 获取文件的共享用户列表
 * @param {string} guid - 文件GUID
 * @param {string} catalogGuid - 目录GUID
 * @returns {Promise}
 */
export const getShareUsers = (guid, catalogGuid) => {
  return http.get('/document/files/share/users', {
    params: { guid, catalogGuid }
  })
}

/**
 * 添加共享用户
 * @param {Object} data - { folderGuid, fileVersionGuid, shareAccount, shareName }
 * @returns {Promise}
 */
export const addShareUser = (data) => http.post('/document/files/share', data)

/**
 * 删除共享用户
 * @param {string} guid - 共享记录GUID
 * @returns {Promise}
 */
export const deleteShareUser = (guid) => http.delete(`/document/files/share/${guid}`)

// ============ 回收站管理 ============

/**
 * 获取回收站文件列表
 * @returns {Promise}
 */
export const getRecycleFiles = () => http.get('/document/recycle')

/**
 * 还原文件
 * @param {string} guid - 文件GUID
 * @returns {Promise}
 */
export const restoreFile = (guid) => http.put(`/document/recycle/${guid}/restore`)

/**
 * 彻底删除文件
 * @param {string} guid - 文件GUID
 * @returns {Promise}
 */
export const permanentDeleteFile = (guid) => http.delete(`/document/recycle/${guid}`)

// ============ 文档统计 ============

/**
 * 获取文档概览统计数据
 * @returns {Promise}
 */
export const getDocumentStats = () => http.get('/document/stats')

/**
 * 获取最近更新的文档列表
 * @param {number} limit - 返回数量限制
 * @returns {Promise}
 */
export const getRecentDocuments = (limit = 10) => {
  return http.get('/document/recent', { params: { limit } })
}

/**
 * 获取待审批文档列表
 * @returns {Promise}
 */
export const getPendingDocuments = () => http.get('/document/pending')

// ============ 文档权限管理 ============

/**
 * 获取目录权限列表
 * @param {string} catalogGuid - 目录GUID
 * @returns {Promise}
 */
export const getFolderPermissions = (catalogGuid) => {
  return http.get(`/document/folder/${catalogGuid}/permissions`)
}

/**
 * 保存目录权限
 * @param {string} catalogGuid - 目录GUID
 * @param {Array} permissions - 权限列表
 * @returns {Promise}
 */
export const saveFolderPermissions = (catalogGuid, permissions) => {
  return http.post(`/document/folder/${catalogGuid}/permissions`, { permissions })
}

/**
 * 获取文件权限列表
 * @param {string} fileGuid - 文件GUID
 * @returns {Promise}
 */
export const getFilePermissions = (fileGuid) => {
  return http.get(`/document/files/${fileGuid}/permissions`)
}

/**
 * 保存文件权限
 * @param {string} fileGuid - 文件GUID
 * @param {Array} permissions - 权限列表
 * @returns {Promise}
 */
export const saveFilePermissions = (fileGuid, permissions) => {
  return http.post(`/document/files/${fileGuid}/permissions`, { permissions })
}

/**
 * 获取文件预览URL
 * @param {string} guid - 文件GUID
 * @returns {string} 预览URL
 */
export const getPreviewUrl = (guid) => {
  const baseUrl = window.__APP_CONFIG__?.API_BASE_URL || 'http://localhost:5260/api'
  return `${baseUrl}/document/files/preview?guid=${guid}`
}
